<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import { ButtonV2, HugeIcon } from '@teachino/shared-design-system';
import { readFragment, type FragmentOf } from '@teachino/shared-infrastructure';
import { $t, type Translation } from '@teachino/shared-i18n';
import { useDateFormat } from '@teachino/shared-util';
import { computed } from 'vue';
import { tenantLicenseFragment } from './tenant-license.fragment';
import { useLicense } from './useLicense';

const { formatDayLongWithYear } = useDateFormat();
const { highlightsForFeatures, openSchoolLicense } = useLicense();

const props = defineProps<{
  license: FragmentOf<typeof tenantLicenseFragment>;
}>();

const license = computed(() =>
  readFragment(tenantLicenseFragment, props.license),
);

const licenseFeatureHighlights = computed<string[]>(() =>
  highlightsForFeatures(license.value.features),
);

const isExpiredSchoolLicense = computed(
  () => license.value.plan.key === 'teachino_school_expired',
);

const FEATURE_ICON_SIZE = 16;
</script>

<template>
  <article class="rounded-lg border border-slate-100 bg-slate-50/70 p-4">
    <div class="mb-3 flex flex-wrap items-center gap-2">
      <span
        class="rounded-md px-2 py-1 text-sm font-semibold"
        :class="
          isExpiredSchoolLicense
            ? 'bg-danger-50 text-danger-700'
            : 'bg-success-50 text-success-700'
        "
      >
        {{ $t(`upgrade.plan.${license.plan.key}` as Translation) }}
      </span>
      <span class="text-sm text-slate-600">
        {{ $t('upgrade.license_page.upgrade.tenant_licenses') }} ·
        {{ license.tenant.name }}
      </span>
    </div>

    <dl class="space-y-2 text-sm text-slate-700">
      <div v-if="license.cycleStartAt && !isExpiredSchoolLicense">
        <dt class="inline">
          {{ $t('upgrade.license_page.license_started_at') }}
        </dt>
        <dd class="ms-1 inline font-medium text-slate-900">
          {{ formatDayLongWithYear(new Date(license.cycleStartAt)) }}
        </dd>
      </div>
      <div v-else-if="license.cycleStartAt">
        <dt class="inline">
          {{ $t('upgrade.license_page.license_expired_at') }}
        </dt>
        <dd class="ms-1 inline font-medium text-slate-900">
          {{ formatDayLongWithYear(new Date(license.cycleStartAt)) }}
        </dd>
      </div>
      <div v-if="license.expiresAt">
        <dt class="inline">
          {{ $t('upgrade.license_page.license_expires_at') }}
        </dt>
        <dd class="ms-1 inline font-medium text-slate-900">
          {{ formatDayLongWithYear(new Date(license.expiresAt)) }}
        </dd>
      </div>
      <div v-else-if="license.cycleRenewalAt">
        <dt class="inline">
          {{ $t('upgrade.license_page.license_renews_at') }}
        </dt>
        <dd class="ms-1 inline font-medium text-slate-900">
          {{ formatDayLongWithYear(new Date(license.cycleRenewalAt)) }}
        </dd>
      </div>
    </dl>

    <ButtonV2
      v-if="isExpiredSchoolLicense"
      class="mt-4"
      variant="primary"
      icon="school"
      :label="$t('upgrade.discover.cta_school_extend')"
      @click="openSchoolLicense()"
    />

    <div v-else class="mt-4 border-t border-slate-100 pt-4">
      <p class="mb-2 text-sm font-semibold text-slate-900">
        {{ $t('upgrade.license_page.features') }}
      </p>
      <ul class="space-y-1 text-sm text-slate-700">
        <li
          v-for="highlight in licenseFeatureHighlights"
          :key="highlight"
          class="flex items-start gap-2"
        >
          <span class="text-primary-600 mt-0.5 shrink-0">
            <HugeIcon icon="checkmark_circle" :size="FEATURE_ICON_SIZE" />
          </span>
          <span>{{ highlight }}</span>
        </li>
      </ul>
    </div>
  </article>
</template>
